import {getElementByElementOrId, identifyElement} from "../helpers/element";
import {MixinStateMachine} from "./mixins/stateable";
import {MixinEvents} from "./mixins/eventable";
import {MixinHtmlClassState} from "./mixins/html_class_state";
import {MixinDatasetConfigurable} from "./mixins/dataset_configurable";
import {MixinUrlConfigurable} from "./mixins/url_configurable";
import {ComponentConfig} from "./component_config";


// Base component class takes a props object.
// Generally includes a `element` property and a `config` property.
// The config property should include a `baseHtmlClass` and `selector`.
export default class Component {

    // Get the standard configuration properties that are used by the component.
    // Subclasses of Component should override `static get config()` with a reference to their own configuration.
    static get config() {
        return ComponentConfig;
    }

    // Convenience function to create instances of Component or Subclasses (eg. MyComponent) from elements in a DOM.
    // Call it from inside a Component subclass like: `this.myComponents = MyComponent.constructComponents({ parentInstance: this });` and you'll get instantiated components of that type in the DOM.
    // @param options = { parentInstance: this, scopeElement: document.body }
    // The `parentInstance` property is an optional class instance (often another component). If provided, it will provide the scope within the DOM for finding component elements. It will also be used when calling the constructor of the component as the second argument.
    // The `scopeElement` property is an optional element. If provided, it will provide the scope within the DOM for finding component elements.
    // If neither `parentInstance` or `scopeElement` are provided, the entire document body will be used as the scope.
    // @returns An array of instantiated components.
    static constructComponents(options = {}) {
        if (!(typeof (Object.getOwnPropertyDescriptor(this, 'config').get) === 'function')) throw new Error('Component.getComponents() componentClass.config is not a getter function. Define a static getter function on the component class that returns the default component configuration');
        const parentInstance = options.parentInstance;
        const scopeElement = parentInstance?.element || options.scopeElement || document.body;
        const elements = Array.from(scopeElement.querySelectorAll(this.config.selector));
        return elements.map((element) => {
            if (!!parentInstance) return new this(element, parentInstance);
            return new this(element);
        });
    }

    static createElement(baseHtmlClass, options = {}) {
        const tagName = options.tagName || 'div';
        let element = document.createElement(tagName);
        const htmlClass = `${baseHtmlClass}`;
        element.setAttribute("class", htmlClass);
        this.content = null;
        return element;
    }

    constructor(props = {}) {
        MixinHtmlClassState(this); // Add the HTML class state management methods.

        MixinEvents(this); // Add the event management methods and set `this.events`.

        MixinDatasetConfigurable(this); // Add methods to gather configuration key/value pairs from the elements dataset attributes.

        MixinUrlConfigurable(this); // Add methods to assist with extracting values from URLs.

        this.render = this.render || function () {
        }; // Placeholder render method. Every component has a render method which Subclasses should override.

        MixinStateMachine(this); // Add the state machine management methods to `this`.
        if (props.hasOwnProperty('stateMachine')) this.setStateMachine(props.stateMachine); // Set `this.stateMachine`
        this.handleStateChange = () => this.render(); // The state machine's handler `handleStateChange` will trigger the component's `render` method.

        this.element = (props.hasOwnProperty('element') ? getElementByElementOrId(props.element) : null);
        this.id = identifyElement(this.element);

        const config = (props.hasOwnProperty('config') ? props.config : {});
        this.configure(config);

        this.storeComponentOnElement();
        this.markElementComponentized(this.componentTypeKey);
    }

    // Set important component properties from any supplied config.
    configure(config) {
        this.config = config;
        this.componentTypeKey = (config.hasOwnProperty('componentTypeKey') ? config.componentTypeKey : null);
        this.baseHtmlClass = (config.hasOwnProperty('baseHtmlClass') ? config.baseHtmlClass : null);
        this.selector = (config.hasOwnProperty('selector') ? config.selector : null);
    }

    storeComponentOnElement() {
        if (!this.element) return;
        if (typeof this.element[Component.config.elementComponentsKey] === "undefined") this.element[Component.config.elementComponentsKey] = [];
        this.element[Component.config.elementComponentsKey].push(this);
    }

    // Sign the component's componentTypeKey string to the element's "componentizedWith" array.
    // The 'ComponentInitializer.isElementComponentized` method checks this property to see if an element is already inited with any specific type of component.
    markElementComponentized(componentTypeKey) {
        if (typeof componentTypeKey !== 'string') return;
        if (this.element.hasOwnProperty(Component.config.componentizedWithKey)) {
            this.element[Component.config.componentizedWithKey].push(componentTypeKey);
        } else {
            this.element[Component.config.componentizedWithKey] = [componentTypeKey];
        }
    }

}
